// One gallery tile on the submit/edit form, matching the Figma edit concept:
// a grip bar across the top (drag to reorder, or focus it and use the arrow
// keys), and two round buttons at the bottom: extract text (OCR) and remove.
// Clicking the image itself opens it full size (handled by the parent).

import { useRef } from "react";
import { Button, Image, Spinner } from "react-bootstrap";
import { FileEarmarkFont, GripHorizontal, XCircle } from "react-bootstrap-icons";

export type GalleryItem =
  | { key: string; kind: "existing"; id: number; url: string }
  | { key: string; kind: "new"; file: File; url: string };

type GalleryImageProps = {
  item: GalleryItem;
  index: number;
  count: number;
  ocrBusy: boolean;
  dragging: boolean;
  onRemove: () => void;
  onOcr: () => void;
  onView: () => void;
  onMove: (delta: number) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDrop: () => void;
};

const GalleryImage = ({
  item,
  index,
  count,
  ocrBusy,
  dragging,
  onRemove,
  onOcr,
  onView,
  onMove,
  onDragStart,
  onDragEnd,
  onDrop,
}: GalleryImageProps) => {
  const tileRef = useRef<HTMLDivElement>(null);
  const position = `${index + 1} of ${count}`;
  const alt = item.kind === "existing" ? "Existing gallery" : "New gallery";

  return (
    <div
      ref={tileRef}
      className={`galleryTile${dragging ? " dragging" : ""}`}
      data-testid="gallery-item"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        onDrop();
      }}
    >
      <button
        type="button"
        className="imageViewButton"
        aria-label={`View image ${position} full size`}
        title="Click to view full size"
        onClick={onView}
      >
        <Image className="recipeImage" src={item.url} alt={alt} draggable={false} />
      </button>

      <div
        className="galleryGrip"
        role="button"
        tabIndex={0}
        aria-label={`Drag to reorder image ${position}`}
        title="Drag to reorder (or use the arrow keys)"
        draggable
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("text/plain", String(index));
          if (tileRef.current) {
            const rect = tileRef.current.getBoundingClientRect();
            e.dataTransfer.setDragImage(tileRef.current, rect.width / 2, rect.height / 2);
          }
          onDragStart();
        }}
        onDragEnd={onDragEnd}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
            e.preventDefault();
            onMove(-1);
          } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
            e.preventDefault();
            onMove(1);
          }
        }}
      >
        <GripHorizontal />
      </div>

      <div className="galleryActions">
        <Button
          type="button"
          variant="light"
          aria-label={`Extract text from image ${position}`}
          title="Extract text from this image into the description"
          disabled={ocrBusy}
          onClick={onOcr}
        >
          {ocrBusy ? <Spinner animation="border" size="sm" role="status" /> : <FileEarmarkFont />}
        </Button>
        <Button
          type="button"
          variant="light"
          aria-label={`Remove image ${position}`}
          title="Remove this image"
          onClick={onRemove}
        >
          <XCircle />
        </Button>
      </div>
    </div>
  );
};

export default GalleryImage;
